import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, User, Briefcase, Loader2, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import toast from 'react-hot-toast';

interface LoginFormProps {
  onClose: () => void;
  defaultToRegister?: boolean;
}

export default function LoginForm({ onClose, defaultToRegister }: LoginFormProps) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(!defaultToRegister);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false); // Not currently used, but kept for potential future use
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn, signUp, signInWithGoogle } = useAuthStore();

  const handleGoogleSignIn = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      await signInWithGoogle();
      onClose();
    } catch (error) {
      console.error('Google sign in error:', error);
      toast.error('Błąd logowania przez Google');
    } finally {
      setIsSubmitting(false);
    }
  };
  const validateForm = () => {
    if (!email || !password) {
      setError('Wypełnij wszystkie pola');
      return false;
    }
    if (!email.includes('@')) {
      setError('Wprowadź poprawny adres email');
      return false;
    }
    if (password.length < 8) {
      setError('Hasło musi mieć minimum 8 znaków');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (!validateForm()) {
      return;
    }


    try {
      setIsSubmitting(true);
      setError(null);

      if (isLogin) {
        await signIn(email, password);
        
        // Przekieruj do dashboardu
        navigate('/dashboard');
      } else {
        await signUp(email, password);

        toast.success('Rejestracja udana! Możesz się teraz zalogować.', {
          duration: 6000
        });

        // Po rejestracji przekieruj do logowania
        setIsLogin(true);
      }
      
      // onClose(); // Don't close on successful registration
    } catch (error: any) {
      console.error('Auth error:', error);

      if (error.message.includes('Invalid login credentials')) {
        setError('Nieprawidłowy email lub hasło');
      } else if (error.message.includes('User already registered')) {
        setError('Ten email jest już zarejestrowany');
      } else {
        setError('Wystąpił błąd. Spróbuj ponownie później.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {!isLogin && (
        <div className="bg-purple-50 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-purple-900">Nie będziesz żałował!</h3>
              <p className="text-sm text-purple-700 mt-1">
                Jan Kowalski już znalazł swoją ścieżkę kariery jako Specjalista AI - czas na Ciebie!
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 mt-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-purple-900">Spersonalizowana analiza</h3>
              <p className="text-sm text-purple-700 mt-1">
                Otrzymasz dokładną analizę Twojej ścieżki kariery i plan rozwoju
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">
          {isLogin ? 'Witaj ponownie!' : 'Dołącz za darmo'}
        </h2>
        <p className="mt-2 text-gray-600">
          {isLogin
            ? 'Zaloguj się, aby kontynuować swoją podróż'
            : 'Stwórz konto i odkryj swoją ścieżkę kariery'}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
              placeholder="twoj@email.pl"
              required
              disabled={isSubmitting}
              autoComplete="username" // Add this for email autofill
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hasło
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
              placeholder="Minimum 8 znaków"
              required
              disabled={isSubmitting}
              minLength={8}
              autoComplete="current-password" // Add this for password autofill
            />
          </div>
        </div>

        {isLogin && (
          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
              Zapamiętaj mnie
            </label>
          </div>
        )}

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {isLogin ? 'Logowanie...' : 'Tworzenie konta...'}
            </>
          ) : (
            <>
              {isLogin ? 'Zaloguj się' : 'Utwórz konto'}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </motion.button>
      </form>

      <div className="text-center">
        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError(null);
          }}
          className="text-sm text-purple-600 hover:text-purple-700"
          disabled={isSubmitting}
        >
          {isLogin
            ? 'Nie masz jeszcze konta? Zarejestruj się'
            : 'Masz już konto? Zaloguj się'}
        </button>
      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
      </div>

      
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">lub</span>
        </div>
      </div>
      
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isSubmitting}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        Kontynuuj z Google
      </button>
    </div>
  );
}
